import { useAppSelector } from 'store/hooks';
import React from 'react';
import { Box, Button, Grid } from '@mui/material';
import { Form } from 'formik';
import * as ct from 'countries-and-timezones';
import * as Yup from 'yup';
import { Permission, Resource } from '@pepperhq/auth-client';
import { LocationGeneralCardComponent } from 'components/location/cards/LocationGeneralCardComponent';
import { Location } from 'components/location/models/LocationModel';
import { MuiForm } from 'lib/Form';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Row } from 'ui/Flex';
import { Panel } from 'ui/Panel';
import { locationApi } from '../LocationApi';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';

export interface LocationGeneralCardProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}

interface LocationGeneralFormValues {
    title: string;
    state: string;
    VATnumber: string;
    phone: string;
    email: string;
    timezone: string;
    description: string;
}

const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required').min(3, 'Title must be at least 3 characters')
});

const stateOptions = [
    { label: 'ACTIVE', value: 'ACTIVE' },
    { label: 'RETIRED', value: 'RETIRED' },
    { label: 'PENDING', value: 'PENDING' }
];

const timezoneOptions = Object.entries(ct.getAllTimezones()).map(tz => ({ label: tz[0], value: tz[0] }));

export const LocationGeneralCard: React.FC<LocationGeneralCardProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const { claims } = useAppSelector(state => state.auth);
    const [isEditing, setIsEditing] = React.useState(false);

    const allowStateEditing = React.useMemo(
        () => claims.hasPermission(Resource.Location, Permission.all),
        [claims]
    );

    const initialValues: LocationGeneralFormValues = React.useMemo(
        () => ({
            title: currentLocation.title ?? '',
            state: currentLocation.state ?? 'PENDING',
            VATnumber: currentLocation.VATnumber ?? '',
            phone: currentLocation.contacts?.phone ?? '',
            email: currentLocation.contacts?.email ?? '',
            timezone: currentLocation.timezone ?? 'Europe/London',
            description: currentLocation.description ?? ''
        }),
        [currentLocation]
    );

    const handleSubmit = React.useCallback(
        async (values: LocationGeneralFormValues) => {
            const locationId = currentLocation._id;
            const { email, phone, timezone, ...rest } = values;
            const response = await locationApi.update(locationId, {
                body: {
                    ...rest,
                    contacts: {
                        email,
                        phone
                    },
                    timezone
                }
            });
            if (response.ok) {
                setCurrentLocation(response.body);
                setIsEditing(false);
            }
        },
        [currentLocation._id, setCurrentLocation]
    );

    const toggleState = React.useCallback(() => {
        setIsEditing(currentValue => !currentValue);
    }, []);

    return (
        <Panel title="General" showAction={!isEditing} onClick={toggleState} divider>
            {isEditing ? (
                <MuiForm<LocationGeneralFormValues>
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ submitForm, isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 6
                                    }}
                                >
                                    <TextFormField
                                        name="title"
                                        label={<MuiFormLabel required>Title</MuiFormLabel>}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 6
                                    }}
                                >
                                    <SelectFormField
                                        name="state"
                                        label="State"
                                        options={stateOptions}
                                        disabled={!allowStateEditing}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 12
                                    }}
                                >
                                    <TextFormField name="VATnumber" label="VAT Number" />
                                </Grid>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 12
                                    }}
                                >
                                    <TextFormField name="phone" label="Phone Number" />
                                </Grid>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 12
                                    }}
                                >
                                    <AutocompleteFormField
                                        name="timezone"
                                        label="Timezone"
                                        options={timezoneOptions}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 12
                                    }}
                                >
                                    <Box marginTop="1px">
                                        <TextFormField name="email" label="Email Address" />
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField name="description" label="Description" multiline />
                                </Grid>
                                <Grid size={12}>
                                    <Row gutter align="flex-end">
                                        <Button onClick={toggleState} variant="outlined">
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={submitForm}
                                            disabled={isSubmitting}
                                            variant="contained"
                                            color="primary"
                                        >
                                            Submit
                                        </Button>
                                    </Row>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </MuiForm>
            ) : (
                <LocationGeneralCardComponent location={currentLocation} />
            )}
        </Panel>
    );
};
